import React from 'react';
import * as Recharts from 'recharts';
import { saBillingFormatDateBR, saBillingFormatDateTimeBR, saBillingFormatMoneyBRL, saBillingMapOverview } from './billing-support.jsx';
import { ApiErrorState } from '../services.jsx';
// Billing overview — view model + Visão geral real (F2).
// Sem import/export. Sem HTTP. Sem Recharts. Sem cálculo financeiro.
  const MONTHLY_EQ_TOOLTIP =
    'Soma dos valores das assinaturas ativas normalizada por mês. Assinaturas anuais são divididas por 12. É um valor contratado e não representa receita confirmada.';

  const OUTSTANDING_HELP =
    'Inclui todas as faturas com status persistido aberto, vencidas ou não.';

  const OVERDUE_HELP =
    'Status calculado pela data de vencimento. Não é um status persistido.';

  const PAID_ADMIN_HELP =
    'Marcação administrativa realizada por um operador. Não representa confirmação de gateway.';

  function money(value) {
    if (typeof saBillingFormatMoneyBRL === 'function') {
      return saBillingFormatMoneyBRL(value);
    }
    return '—';
  }

  function dateBR(value) {
    if (typeof saBillingFormatDateBR === 'function') {
      return saBillingFormatDateBR(value);
    }
    return '—';
  }

  function dateTimeBR(value) {
    if (typeof saBillingFormatDateTimeBR === 'function') {
      return saBillingFormatDateTimeBR(value);
    }
    return '—';
  }

  function asObject(value) {
    return value && typeof value === 'object' && typeof value.map !== 'function'
      ? value
      : {};
  }

  function ensureList(value) {
    return value && typeof value === 'object' && typeof value.map === 'function'
      ? value
      : [];
  }

  function countOrDash(value) {
    return value ?? '—';
  }

  function saBillingBuildOverviewViewModel(payload) {
    const mapped = typeof saBillingMapOverview === 'function'
      ? saBillingMapOverview(payload)
      : (payload && typeof payload === 'object' ? Object.assign({}, payload) : {});

    const totals = asObject(mapped.totals);
    const byPlan = ensureList(mapped.by_plan);
    const subscriptions = asObject(mapped.subscriptions);
    const invoices = asObject(mapped.invoices);
    const period = asObject(mapped.period);

    const snapshot = {
      today_formatted: dateBR(period.today),
      month_start_formatted: dateBR(period.month_start),
      month_end_formatted: dateBR(period.month_end),
      updated_at_formatted: mapped.updated_at_formatted != null
        ? mapped.updated_at_formatted
        : dateTimeBR(mapped.updated_at),
      timezone: period.timezone ?? '—',
    };

    const primary_cards = [
      {
        id: 'subscriptions_active',
        label: 'Assinaturas ativas',
        value: countOrDash(subscriptions.active),
        help: `de ${countOrDash(subscriptions.total)} cadastradas`,
      },
      {
        id: 'active_monthly_equivalent',
        label: 'Equivalente mensal contratado',
        value: subscriptions.active_monthly_equivalent_formatted
          ?? money(subscriptions.active_monthly_equivalent),
        help: 'Valor contratado normalizado por mês',
        tooltip: MONTHLY_EQ_TOOLTIP,
      },
      {
        id: 'outstanding_amount',
        label: 'Faturas em aberto',
        value: invoices.outstanding_amount_formatted
          ?? money(invoices.outstanding_amount),
        help: `${countOrDash(invoices.outstanding_count)} faturas abertas`,
        description: OUTSTANDING_HELP,
      },
      {
        id: 'overdue_amount',
        label: 'Faturas vencidas',
        value: invoices.overdue_amount_formatted
          ?? money(invoices.overdue_amount),
        help: `${countOrDash(invoices.overdue)} faturas vencidas`,
        description: OVERDUE_HELP,
        tone: 'danger',
      },
    ];

    const secondary_cards = [
      {
        id: 'tenants_trial',
        label: 'Tenants em trial',
        value: countOrDash(totals.trial),
      },
      {
        id: 'tenants_past_due',
        label: 'Tenants inadimplentes',
        value: countOrDash(totals.past_due),
        tone: 'danger',
      },
      {
        id: 'paid_this_month',
        label: 'Marcadas como pagas no mês',
        value: invoices.paid_this_month_amount_formatted
          ?? money(invoices.paid_this_month_amount),
        help: `${countOrDash(invoices.paid_this_month_count)} marcações`,
        description: PAID_ADMIN_HELP,
      },
      {
        id: 'due_within_7_days',
        label: 'Faturas que vencem em 7 dias',
        value: countOrDash(invoices.due_within_7_days),
      },
    ];

    const subscription_status_rows = ensureList(subscriptions.by_status).map((row) => ({
      code: row.code,
      label: row.label ?? row.code ?? '—',
      count: row.count ?? 0,
    }));

    const subscription_plan_rows = ensureList(subscriptions.active_by_plan).map((row) => {
      const label = row.label ?? row.code ?? '—';
      const code = row.code ?? '';
      return {
        code,
        label,
        show_code: code && label !== code,
        count: row.count ?? 0,
        monthly_equivalent_formatted: money(row.monthly_equivalent),
      };
    });

    const subscription_cycle_rows = ensureList(subscriptions.active_by_billing_cycle).map((row) => ({
      code: row.code,
      label: row.label ?? row.code ?? '—',
      count: row.count ?? 0,
      monthly_equivalent_formatted: money(row.monthly_equivalent),
    }));

    const subscription_movement_rows = [
      { id: 'new', label: 'Novas', value: countOrDash(subscriptions.new_this_month) },
      { id: 'ended', label: 'Encerradas', value: countOrDash(subscriptions.ended_this_month) },
      { id: 'cancelled', label: 'Canceladas', value: countOrDash(subscriptions.cancelled_this_month) },
    ];

    const subscription_due_rows = [
      {
        id: 'overdue',
        label: 'Vencidas',
        value: countOrDash(subscriptions.next_due_overdue),
        tone: 'danger',
      },
      {
        id: 'within_7',
        label: 'Próximos 7 dias',
        value: countOrDash(subscriptions.next_due_within_7_days),
      },
      {
        id: 'within_30',
        label: 'Próximos 30 dias',
        value: countOrDash(subscriptions.next_due_within_30_days),
        note: 'A janela de 30 dias contém a de 7 dias.',
      },
    ];

    const invoice_status_rows = ensureList(invoices.by_display_status).map((row) => ({
      code: row.code,
      label: row.label ?? row.code ?? '—',
      count: row.count ?? 0,
      amount_formatted: money(row.amount),
    }));

    const invoice_movement_rows = [
      {
        id: 'paid',
        label: 'Marcadas como pagas',
        count: countOrDash(invoices.paid_this_month_count),
        amount_formatted: invoices.paid_this_month_amount_formatted
          ?? money(invoices.paid_this_month_amount),
        description: PAID_ADMIN_HELP,
      },
      {
        id: 'cancelled',
        label: 'Canceladas',
        count: countOrDash(invoices.cancelled_this_month_count),
        amount_formatted: invoices.cancelled_this_month_amount_formatted
          ?? money(invoices.cancelled_this_month_amount),
      },
    ];

    const invoice_due_rows = [
      {
        id: 'within_7',
        label: 'Próximos 7 dias',
        value: countOrDash(invoices.due_within_7_days),
      },
      {
        id: 'within_30',
        label: 'Próximos 30 dias',
        value: countOrDash(invoices.due_within_30_days),
        note: 'A janela de 30 dias contém a de 7 dias.',
      },
    ];

    const commercial_status_rows = [
      { id: 'tenants', label: 'Tenants', value: countOrDash(totals.tenants) },
      { id: 'trial', label: 'Trial', value: countOrDash(totals.trial) },
      { id: 'active', label: 'Ativos', value: countOrDash(totals.active) },
      { id: 'past_due', label: 'Inadimplentes', value: countOrDash(totals.past_due) },
      { id: 'suspended', label: 'Suspensos', value: countOrDash(totals.suspended) },
      { id: 'cancelled', label: 'Cancelados', value: countOrDash(totals.cancelled) },
    ];

    const commercial_plan_rows = byPlan.map((row) => ({
      plan: row.plan ?? '—',
      trial: row.trial ?? 0,
      active: row.active ?? 0,
      past_due: row.past_due ?? 0,
      suspended: row.suspended ?? 0,
      cancelled: row.cancelled ?? 0,
    }));

    const subsTotal = subscriptions.total ?? 0;
    const invTotal = invoices.total ?? 0;
    const is_empty = subsTotal === 0 && invTotal === 0;

    return {
      snapshot,
      primary_cards,
      secondary_cards,
      subscription_status_rows,
      subscription_plan_rows,
      subscription_cycle_rows,
      subscription_movement_rows,
      subscription_due_rows,
      invoice_status_rows,
      invoice_movement_rows,
      invoice_due_rows,
      commercial_status_rows,
      commercial_plan_rows,
      period_label: `${snapshot.month_start_formatted} até ${snapshot.month_end_formatted}`,
      is_empty,
    };
  }

  function OverviewCard({ card, onActivate }) {
    const helpId = card.tooltip ? `billing-overview-help-${card.id}` : null;
    const descId = card.description ? `billing-overview-desc-${card.id}` : null;
    const describedBy = [helpId, descId].filter(Boolean).join(' ') || undefined;
    const toneClass = card.tone === 'danger' ? ' billing-overview-card--danger' : '';
    const actionClass = onActivate ? ' billing-overview-card--action' : '';
    const actionLabel = onActivate
      ? `Abrir detalhes de ${card.label.toLowerCase()}`
      : undefined;
    const content = (
      <>
        <div className="billing-overview-card-label">
          <span>{card.label}</span>
          {card.tooltip && (
            <span
              className="billing-overview-card-info"
              title={card.tooltip}
              aria-hidden="true"
            >
              i
            </span>
          )}
        </div>
        <div className="billing-overview-card-value">{card.value}</div>
        {card.help != null && card.help !== '' && (
          <div className="billing-overview-card-help">{card.help}</div>
        )}
        {card.description && (
          <div id={descId} className="billing-overview-card-desc">{card.description}</div>
        )}
        {card.tooltip && (
          <span id={helpId} className="billing-sr-only">{card.tooltip}</span>
        )}
      </>
    );

    if (onActivate) {
      return (
        <button
          type="button"
          className={`billing-overview-card${toneClass}${actionClass}`}
          onClick={onActivate}
          aria-label={actionLabel}
          aria-describedby={describedBy}
        >
          {content}
        </button>
      );
    }

    return (
      <div
        className={`billing-overview-card${toneClass}`}
        aria-describedby={describedBy}
      >
        {content}
      </div>
    );
  }

  function OverviewSkeleton() {
    return (
      <div
        className="billing-overview billing-overview-skeleton"
        role="status"
        aria-label="Carregando visão geral de Billing"
        aria-busy="true"
      >
        <div className="billing-overview-kpi-grid">
          {[0, 1, 2, 3].map((i) => (
            <div key={`p-${i}`} className="billing-overview-card billing-overview-skel-block" />
          ))}
        </div>
        <div className="billing-overview-secondary-grid">
          {[0, 1, 2, 3].map((i) => (
            <div key={`s-${i}`} className="billing-overview-card billing-overview-skel-block" />
          ))}
        </div>
        <div className="billing-overview-breakdown-grid">
          {[0, 1, 2].map((i) => (
            <div key={`b-${i}`} className="billing-overview-section billing-overview-skel-block billing-overview-skel-section" />
          ))}
        </div>
      </div>
    );
  }

  function RowList({ rows, emptyLabel, onActivate, listLabel }) {
    if (!rows || rows.length === 0) {
      return <div className="billing-overview-empty-local">{emptyLabel}</div>;
    }
    return (
      <ul className="billing-overview-list" aria-label={listLabel}>
        {rows.map((row) => {
          const key = row.code || row.id || row.label;
          const rowActionLabel = onActivate
            ? `Abrir lista filtrada por ${row.label}`
            : undefined;
          const body = (
            <>
              <div className="billing-overview-row-main">
                <span className="billing-overview-row-label">{row.label}</span>
                {row.show_code && (
                  <span className="billing-overview-row-code">{row.code}</span>
                )}
                {row.note && (
                  <span className="billing-overview-row-note">{row.note}</span>
                )}
              </div>
              <div className="billing-overview-row-value">
                {row.count != null && row.amount_formatted != null ? (
                  <>
                    <span>{row.count}</span>
                    <span className="billing-overview-row-amount">{row.amount_formatted}</span>
                  </>
                ) : row.monthly_equivalent_formatted != null ? (
                  <>
                    <span>{row.count}</span>
                    <span className="billing-overview-row-amount">{row.monthly_equivalent_formatted}</span>
                  </>
                ) : (
                  <span className={row.tone === 'danger' ? 'billing-overview-tone-danger' : undefined}>
                    {row.value != null ? row.value : row.count}
                  </span>
                )}
              </div>
            </>
          );

          return (
            <li key={key} className="billing-overview-row">
              {onActivate ? (
                <button
                  type="button"
                  className="billing-overview-row-action"
                  onClick={onActivate}
                  aria-label={rowActionLabel}
                >
                  {body}
                </button>
              ) : body}
            </li>
          );
        })}
      </ul>
    );
  }

  function MovementList({ rows }) {
    return (
      <ul className="billing-overview-list">
        {rows.map((row) => (
          <li key={row.id || row.label} className="billing-overview-row">
            <div className="billing-overview-row-main">
              <span className="billing-overview-row-label">{row.label}</span>
              {row.description && (
                <span className="billing-overview-row-note">{row.description}</span>
              )}
            </div>
            <div className="billing-overview-row-value">
              {row.amount_formatted != null ? (
                <>
                  <span>{row.count}</span>
                  <span className="billing-overview-row-amount">{row.amount_formatted}</span>
                </>
              ) : (
                <span>{row.value}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  }

  function SaBillingOverview({ overview, loading, error, onRetry, onOpenCommercialStatus, onOpenSubscriptions, onOpenInvoices }) {
    if (loading) {
      return (
        <div className="billing-overview">
          <div className="billing-overview-header">
            <div>
              <h2 className="billing-overview-title">Visão geral de Billing</h2>
            </div>
          </div>
          <OverviewSkeleton />
        </div>
      );
    }

    if (error) {
      const ErrorComp = typeof ApiErrorState === 'function' ? ApiErrorState : null;
      if (ErrorComp) {
        return <ErrorComp error={error} onRetry={onRetry} />;
      }
      return (
        <div className="billing-overview billing-overview-error" role="alert">
          <div className="billing-overview-error-title">Falha ao obter dados</div>
          <div className="billing-overview-error-msg">{error}</div>
          {onRetry && (
            <button type="button" className="btn" onClick={onRetry}>Tentar novamente</button>
          )}
        </div>
      );
    }

    const vm = saBillingBuildOverviewViewModel(overview);

    return (
      <div className="billing-overview">
        <div className="billing-overview-header">
          <div>
            <h2 className="billing-overview-title">Visão geral de Billing</h2>
            <div className="billing-overview-meta">
              <span>Snapshot: {vm.snapshot.today_formatted}</span>
              <span>Atualizado: {vm.snapshot.updated_at_formatted}</span>
              <span>Timezone: {vm.snapshot.timezone}</span>
            </div>
          </div>
          {onRetry && (
            <button type="button" className="btn" onClick={onRetry} aria-busy={loading || undefined}>
              Atualizar
            </button>
          )}
        </div>

        {vm.is_empty && (
          <div className="billing-overview-empty">
            <div className="billing-overview-empty-title">
              Nenhuma assinatura ou fatura interna cadastrada
            </div>
            <div className="billing-overview-empty-desc">
              O status comercial dos tenants continua disponível. Assinaturas e faturas aparecerão aqui após o cadastro interno.
            </div>
            {onOpenCommercialStatus && (
              <button type="button" className="btn" onClick={onOpenCommercialStatus}>
                Abrir Status comercial
              </button>
            )}
          </div>
        )}

        {!vm.is_empty && (
          <>
            <div className="billing-overview-kpi-grid">
              {vm.primary_cards.map((card) => {
                let onActivate;
                if (card.id === 'subscriptions_active' && onOpenSubscriptions) {
                  onActivate = onOpenSubscriptions;
                } else if (
                  (card.id === 'outstanding_amount' || card.id === 'overdue_amount')
                  && onOpenInvoices
                ) {
                  onActivate = onOpenInvoices;
                }
                return (
                  <OverviewCard
                    key={card.id}
                    card={card}
                    onActivate={onActivate}
                  />
                );
              })}
            </div>

            <div className="billing-overview-secondary-grid">
              {vm.secondary_cards.map((card) => {
                const invoiceSecondary = card.id === 'paid_this_month' || card.id === 'due_within_7_days';
                return (
                  <OverviewCard
                    key={card.id}
                    card={card}
                    onActivate={invoiceSecondary && onOpenInvoices ? onOpenInvoices : undefined}
                  />
                );
              })}
            </div>

            <div className="billing-overview-breakdown-grid">
              <section className="billing-overview-section" aria-labelledby="billing-overview-subscriptions-status">
                <div className="billing-overview-section-head">
                  <div className="billing-overview-section-head-row">
                    <h3 id="billing-overview-subscriptions-status">Assinaturas por status</h3>
                    {onOpenSubscriptions && (
                      <button type="button" className="btn" onClick={onOpenSubscriptions}>
                        Ver assinaturas
                      </button>
                    )}
                  </div>
                </div>
                <RowList
                  rows={vm.subscription_status_rows}
                  emptyLabel="Nenhum status de assinatura."
                  onActivate={onOpenSubscriptions}
                  listLabel="Assinaturas por status"
                />
              </section>

              <section className="billing-overview-section" aria-labelledby="billing-overview-subscriptions-plan">
                <div className="billing-overview-section-head">
                  <div className="billing-overview-section-head-row">
                    <h3 id="billing-overview-subscriptions-plan">Assinaturas ativas por plano</h3>
                    {onOpenSubscriptions && (
                      <button type="button" className="btn" onClick={onOpenSubscriptions}>
                        Ver assinaturas
                      </button>
                    )}
                  </div>
                </div>
                <RowList
                  rows={vm.subscription_plan_rows}
                  emptyLabel="Nenhuma assinatura ativa por plano."
                  onActivate={onOpenSubscriptions}
                  listLabel="Assinaturas ativas por plano"
                />
              </section>

              <section className="billing-overview-section" aria-labelledby="billing-overview-subscriptions-cycle">
                <div className="billing-overview-section-head">
                  <h3 id="billing-overview-subscriptions-cycle">Assinaturas ativas por ciclo</h3>
                </div>
                <RowList rows={vm.subscription_cycle_rows} emptyLabel="Nenhum ciclo de cobrança." listLabel="Assinaturas ativas por ciclo" />
              </section>
            </div>

            <div className="billing-overview-breakdown-grid">
              <section className="billing-overview-section" aria-labelledby="billing-overview-invoices-status">
                <div className="billing-overview-section-head">
                  <div className="billing-overview-section-head-row">
                    <h3 id="billing-overview-invoices-status">Faturas por status</h3>
                    {onOpenInvoices && (
                      <button type="button" className="btn" onClick={onOpenInvoices}>
                        Ver faturas
                      </button>
                    )}
                  </div>
                </div>
                <RowList
                  rows={vm.invoice_status_rows}
                  emptyLabel="Nenhum status de fatura."
                  onActivate={onOpenInvoices}
                  listLabel="Faturas por status"
                />
              </section>

              <section className="billing-overview-section" aria-labelledby="billing-overview-subscription-movement">
                <div className="billing-overview-section-head">
                  <h3 id="billing-overview-subscription-movement">Movimentação do mês — assinaturas</h3>
                  <span className="billing-overview-section-sub">{vm.period_label}</span>
                </div>
                <MovementList rows={vm.subscription_movement_rows} />
              </section>

              <section className="billing-overview-section" aria-labelledby="billing-overview-invoice-movement">
                <div className="billing-overview-section-head">
                  <h3 id="billing-overview-invoice-movement">Movimentação do mês — faturas</h3>
                  <span className="billing-overview-section-sub">{vm.period_label}</span>
                </div>
                <MovementList rows={vm.invoice_movement_rows} />
              </section>
            </div>

            <div className="billing-overview-breakdown-grid">
              <section className="billing-overview-section" aria-labelledby="billing-overview-subscription-due">
                <div className="billing-overview-section-head">
                  <h3 id="billing-overview-subscription-due">Próximos vencimentos — assinaturas</h3>
                </div>
                <RowList rows={vm.subscription_due_rows} emptyLabel="Sem vencimentos." listLabel="Próximos vencimentos de assinaturas" />
              </section>

              <section className="billing-overview-section" aria-labelledby="billing-overview-invoice-due">
                <div className="billing-overview-section-head">
                  <h3 id="billing-overview-invoice-due">Próximos vencimentos — faturas</h3>
                </div>
                <RowList rows={vm.invoice_due_rows} emptyLabel="Sem vencimentos." listLabel="Próximos vencimentos de faturas" />
              </section>
            </div>
          </>
        )}

        <section className="billing-overview-section billing-overview-commercial" aria-labelledby="billing-overview-commercial-title">
          <div className="billing-overview-section-head">
            <h3 id="billing-overview-commercial-title">Status comercial</h3>
          </div>
          <dl className="billing-overview-status-grid">
            {vm.commercial_status_rows.map((row) => (
              <div key={row.id} className="billing-overview-card billing-overview-card--compact">
                <dt className="billing-overview-card-label">{row.label}</dt>
                <dd className="billing-overview-card-value">{row.value}</dd>
              </div>
            ))}
          </dl>

          {vm.commercial_plan_rows.length > 0 && (
            <div className="billing-overview-plan-table-wrap">
              <table className="billing-overview-plan-table">
                <caption className="billing-sr-only">
                  Distribuição de tenants por plano e status comercial
                </caption>
                <thead>
                  <tr>
                    <th>Plano</th>
                    <th>Trial</th>
                    <th>Ativos</th>
                    <th>Inadimplentes</th>
                    <th>Suspensos</th>
                    <th>Cancelados</th>
                  </tr>
                </thead>
                <tbody>
                  {vm.commercial_plan_rows.map((row) => (
                    <tr key={row.plan}>
                      <td>{row.plan}</td>
                      <td>{row.trial}</td>
                      <td>{row.active}</td>
                      <td>{row.past_due}</td>
                      <td>{row.suspended}</td>
                      <td>{row.cancelled}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    );
  }

export {
  SaBillingOverview,
  saBillingBuildOverviewViewModel,
};
